import axios from 'axios';

/**
 * Browser heartbeat for the team statistics: which order / page area is actively worked on, per minute. The
 * backend stores no person (ApiActivityController) — it measures the effort of trips and process steps.
 * Counts only while this tab is visible AND focused AND there was input in the last IDLE_MS; reading without
 * touching anything for longer is not counted. Only the focused tab reports, so several tabs never double count.
 * The area is the [data-activity] block of the last input (sidebar.todos, itinerary.hotel, email-compose …) while
 * that block is still on the page, otherwise the page from the URL.
 */
const TICK_MS = 15 * 1000;
const FLUSH_MS = 60 * 1000;
const IDLE_MS = 2 * 60 * 1000;
const ENDPOINT = '/api/activity/heartbeat';
const heartbeatClient = axios.create();

/**
 * /orders/123/calculations → {orderId: 123, area: 'calculations'}; /orders/123 → itinerary (the days);
 * everything else → first path segment (todos, emails, providers, statistics …)
 */
export function areaFromPath(pathname) {
    const parts = pathname.split('/').filter(Boolean);
    if (parts[0] === 'orders' && /^\d+$/.test(parts[1] || '')) {
        return { orderId: parseInt(parts[1], 10), area: (parts[2] || 'itinerary').toLowerCase() };
    }
    return { orderId: null, area: (parts[0] || 'home').toLowerCase() };
}

/**
 * The innermost [data-activity] block around an event target, null outside of one (document, window, text nodes)
 */
export function zoneOf(target) {
    const element = target && target.nodeType === 3 ? target.parentElement : target;
    if (!element || typeof element.closest !== 'function') {
        return null;
    }
    return element.closest('[data-activity]');
}

export function installActivityHeartbeat() {
    let lastInput = Date.now();
    let lastZone = null;
    let pending = {};

    // mousemove only keeps the timer alive: hovering over a block is no work in it
    const onInput = event => {
        lastInput = Date.now();
        lastZone = zoneOf(event.target);
    };
    const inputEvents = ['keydown', 'mousedown', 'wheel', 'touchstart', 'scroll'];
    inputEvents.forEach(name => window.addEventListener(name, onInput, { passive: true, capture: true }));
    // mousemove fires constantly — a cheap throttle is enough
    let lastMove = 0;
    const onMove = () => {
        const now = Date.now();
        if (now - lastMove > 5000) { lastMove = now; lastInput = now; }
    };
    window.addEventListener('mousemove', onMove, { passive: true });

    const tick = () => {
        const now = Date.now();
        if (document.visibilityState !== 'visible' || !document.hasFocus() || now - lastInput > IDLE_MS) {
            return;
        }
        const minute = now - (now % 60000);
        const item = { at: minute, ...areaFromPath(window.location.pathname) };
        // modals are only hidden (v-show): a closed one must not keep the minutes
        if (lastZone && lastZone.isConnected && lastZone.getClientRects().length) {
            item.area = lastZone.getAttribute('data-activity');
        }
        pending[minute] = item;
    };

    const flush = (keepalive = false) => {
        const minutes = Object.values(pending);
        if (!minutes.length) { return; }
        pending = {};
        const body = JSON.stringify({ minutes });
        if (keepalive && window.fetch) {
            // the page is going away: axios would be cancelled, fetch keepalive survives and can send the JWT header
            const headers = { 'Content-Type': 'application/json' };
            const auth = axios.defaults.headers.common.Authorization;
            if (auth) { headers.Authorization = auth; }
            fetch(ENDPOINT, { method: 'POST', body, headers, keepalive: true, credentials: 'same-origin' }).catch(() => {});
            return;
        }
        // own instance: the global interceptor (@api) would log out on 401 and toast errors — a lost heartbeat is
        // neither; the minutes go back for the next try
        heartbeatClient.post(ENDPOINT, body, { headers: { 'Content-Type': 'application/json', Authorization: axios.defaults.headers.common.Authorization } })
            .catch(() => { minutes.forEach(item => { pending[item.at] = pending[item.at] || item; }); });
    };

    const onHide = () => flush(true);
    const onVisibility = () => { if (document.visibilityState === 'hidden') { flush(true); } };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisibility);

    const tickTimer = setInterval(tick, TICK_MS);
    const flushTimer = setInterval(() => flush(false), FLUSH_MS);
    tick();

    return () => {
        clearInterval(tickTimer);
        clearInterval(flushTimer);
        flush(true);
        inputEvents.forEach(name => window.removeEventListener(name, onInput, { capture: true }));
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('pagehide', onHide);
        document.removeEventListener('visibilitychange', onVisibility);
    };
}
